import { useSortable } from "@dnd-kit/sortable";
import React, { memo, type ReactNode } from "react";

import {
  type KanbanCardSortableBindings,
  RankReorderCardContext,
} from "@/components/organisms/custom/ui/KanbanPlayerCard/rankReorderContext";

/**
 * Props for RankSortableCard.
 */
interface RankSortableCardProps {
  /** Phase/column the card belongs to — scopes the reorder to one column. */
  columnId: string;
  /** Player id — the sortable node id is derived from it (`kanban-${playerId}`). */
  playerId: string;
  /**
   * SE-6554: the player payload the drag overlay renders from — the same fields
   * a card's own `useDraggable` carries. Without it a rank drag would put an
   * empty card under the cursor, because this wrapper's data was identity only
   * (`dragType`/`columnId`/`playerId`) back when rank drags rendered no overlay.
   */
  dragData?: Record<string, unknown>;
  /**
   * The player card. It reads the sortable bindings from context, makes its own
   * drag box the sortable node, and renders the grab handle inside it.
   */
  children: ReactNode;
}

/**
 * SE-4685: Wraps a player card in a within-column rank drag-reorder affordance,
 * rendered only when a column is sorted by Rank. It registers the dnd-kit
 * sortable node (id `kanban-${playerId}`) and shares the bindings with the card
 * via RankReorderCardContext, so the card's own drag box carries the whole-row
 * transform and renders the grab handle inside the transformed node (no seam).
 * The drag data carries `dragType: "rank-reorder"` plus `columnId`/`playerId`,
 * which MainContainer's drag-end handler uses to renumber and persist positions.
 * This wrapper renders no host node of its own, so it needs no runtime styles.
 * @param root0 - Component props
 * @param root0.columnId - Phase/column the card belongs to
 * @param root0.playerId - Player id used to derive the sortable node id
 * @param root0.dragData - SE-6554: player payload the drag overlay renders from
 * @param root0.children - The player card that consumes the sortable bindings
 */
const RankSortableCardContainer = ({
  columnId,
  playerId,
  dragData,
  children,
}: RankSortableCardProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `kanban-${playerId}`,
    // The payload comes first so the identity fields below can never be
    // overwritten by it: MainContainer routes the whole drop on `dragType`.
    data: {
      ...dragData,
      dragType: "rank-reorder",
      columnId,
      playerId,
    },
  });

  const sortable: KanbanCardSortableBindings = {
    setNodeRef,
    transform,
    transition,
    isDragging,
    attributes: attributes as unknown as Record<string, unknown>,
    listeners: listeners as unknown as Record<string, unknown> | undefined,
  };

  return (
    <RankReorderCardContext.Provider value={sortable}>
      {children}
    </RankReorderCardContext.Provider>
  );
};

RankSortableCardContainer.displayName = "RankSortableCardContainer";

export default memo(RankSortableCardContainer);
